import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import Seo from "@/components/seo/Seo";
import { resultsService } from "@/api/results";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
    CheckCircle,
    XCircle,
    MinusCircle,
    Clock,
    LockSimple,
    Crown,
    Lightbulb,
    CaretDown,
} from "@phosphor-icons/react";
import api from "@/api/client";
import ShareSlip from "@/components/feature/ShareSlip";

const STATUS_CONFIG = {
    won: { icon: CheckCircle, label: "WON", color: "text-primary", bar: "bg-primary" },
    lost: { icon: XCircle, label: "LOST", color: "text-red-500", bar: "bg-red-500" },
    void: { icon: MinusCircle, label: "VOID", color: "text-zinc-400", bar: "bg-zinc-500" },
    pending: { icon: Clock, label: "PENDING", color: "text-amber-400", bar: "bg-amber-400" },
};

const slug = (s) =>
    (s || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");

export default function Results() {
    const { user } = useAuth();
    const [params, setParams] = useSearchParams();
    const leagueParam = params.get("league") || "";
    const isVip = user?.userLevel === "vip";

    const [yesterday, setYesterday] = useState(null);
    const [recent, setRecent] = useState([]);
    const [leagues, setLeagues] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const [y, r, l] = await Promise.all([
                    api
                        .get("/results/yesterday", { params: leagueParam ? { league: leagueParam } : {} })
                        .then((res) => res.data),
                    resultsService.recent(7),
                    api.get("/results/leagues").then((res) => res.data),
                ]);
                setYesterday(y);
                setRecent(r?.items || []);
                setLeagues(l?.items || []);
            } catch (err) {
                // eslint-disable-next-line no-console
                console.warn("Results load failed:", err?.message || err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [leagueParam, user]);

    const onSelectLeague = (name) => {
        const next = new URLSearchParams(params);
        if (name) next.set("league", name);
        else next.delete("league");
        setParams(next, { replace: false });
    };

    const summary = yesterday?.summary;
    const locked = yesterday?.locked || [];

    const jsonLd = yesterday
        ? {
              "@context": "https://schema.org",
              "@type": "ItemList",
              name: `KickLoom yesterday's slip — ${yesterday.date}${leagueParam ? ` · ${leagueParam}` : ""}`,
              numberOfItems:
                  (yesterday.tiers["2"]?.length || 0) +
                  (yesterday.tiers["5"]?.length || 0) +
                  (yesterday.tiers["10"]?.length || 0),
              itemListElement: ["2", "5", "10"].flatMap((cat) =>
                  (yesterday.tiers[cat] || []).map((p, i) => ({
                      "@type": "ListItem",
                      position: i + 1,
                      name: `${p.match} — ${p.prediction}`,
                  })),
              ),
          }
        : undefined;

    const path = leagueParam ? `/results?league=${encodeURIComponent(leagueParam)}` : "/results";
    const seoTitle = leagueParam ? `${leagueParam} Results` : "Yesterday's Results";
    const seoDescription = leagueParam
        ? `KickLoom prediction results filtered to ${leagueParam}. Free public 2-game slip, verified against real fixture scores.`
        : "Transparent record of every KickLoom prediction. Daily slip hit-rate, ROI and pick-by-pick outcomes — verified against real fixture scores.";

    return (
        <Layout>
            <Seo title={seoTitle} description={seoDescription} path={path} jsonLd={jsonLd} />

            <section className="max-w-7xl mx-auto px-4 md:px-8 pt-16">
                <div className="font-mono text-xs uppercase tracking-[0.4em] text-primary mb-3">
                    ▌ TRANSPARENT TRACK RECORD
                </div>
                <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tighter">
                    Yesterday's Slip
                </h1>
                <p className="mt-4 text-zinc-400 max-w-2xl">
                    Every free 2-game pick — scored against real fixture results from
                    API-Football. No selective publishing.
                    {!isVip && " VIP unlocks the 5 and 10-game slip history."}
                </p>
            </section>

            {/* League filter */}
            {leagues.length > 1 && (
                <section className="max-w-7xl mx-auto px-4 md:px-8 mt-8">
                    <div className="font-mono text-xs uppercase tracking-widest text-zinc-500 mb-2">
                        FILTER BY LEAGUE
                    </div>
                    <div
                        className="flex flex-wrap gap-2"
                        data-testid="league-filter"
                    >
                        <LeagueChip
                            active={!leagueParam}
                            onClick={() => onSelectLeague("")}
                            testId="league-chip-all"
                        >
                            All leagues
                        </LeagueChip>
                        {leagues.slice(0, 12).map((l) => (
                            <LeagueChip
                                key={l.league}
                                active={leagueParam === l.league}
                                onClick={() => onSelectLeague(l.league)}
                                testId={`league-chip-${slug(l.league)}`}
                            >
                                {l.league}
                            </LeagueChip>
                        ))}
                    </div>
                </section>
            )}

            {/* Summary row */}
            <section className="max-w-7xl mx-auto px-4 md:px-8 py-10">
                {loading ? (
                    <p className="font-mono text-xs uppercase tracking-widest text-zinc-500 pulse-sharp">
                        LOADING...
                    </p>
                ) : summary && summary.total > 0 ? (
                    <div
                        className="grid grid-cols-2 md:grid-cols-5 gap-4"
                        data-testid="results-summary"
                    >
                        <SummaryCell label="Date" value={yesterday.date} mono />
                        <SummaryCell label="Picks shown" value={summary.total} mono />
                        <SummaryCell
                            label="Won"
                            value={`${summary.won}/${summary.won + summary.lost}`}
                            highlight
                        />
                        <SummaryCell label="Hit-rate" value={`${summary.hitRate}%`} highlight />
                        <SummaryCell
                            label="ROI (1u/pick)"
                            value={`${summary.roi >= 0 ? "+" : ""}${summary.roi}%`}
                            highlight={summary.roi >= 0}
                            negative={summary.roi < 0}
                        />
                    </div>
                ) : (
                    <div className="border border-dashed border-border p-10 text-center">
                        <p className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                            {leagueParam
                                ? `No ${leagueParam} picks settled yet.`
                                : "No picks were settled yesterday."}
                        </p>
                    </div>
                )}
            </section>

            {/* Free public tier 2 */}
            {yesterday && (yesterday.tiers["2"] || []).length > 0 && (
                <section className="max-w-7xl mx-auto px-4 md:px-8 pb-12">
                    <div className="flex items-baseline justify-between mb-4 border-b border-border pb-3">
                        <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                            <span className="text-primary font-mono">2</span>-game slip
                            <span className="ml-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                                FREE
                            </span>
                        </h2>
                        <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                            {yesterday.tiers["2"].filter((i) => i.resultStatus === "won").length}/
                            {yesterday.tiers["2"].length} won
                        </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {yesterday.tiers["2"].map((p, i) => (
                            <ResultCard
                                key={i}
                                pick={p}
                                date={yesterday.date}
                                testIdPrefix={`result-2-${i}`}
                            />
                        ))}
                    </div>
                </section>
            )}

            {/* VIP tiers — locked for non-VIP */}
            {["5", "10"].map((cat) => {
                const items = yesterday?.tiers?.[cat] || [];
                const isLocked = locked.includes(cat);
                if (!isLocked && items.length === 0) return null;
                return (
                    <section
                        key={cat}
                        className="max-w-7xl mx-auto px-4 md:px-8 pb-12"
                        data-testid={`vip-tier-${cat}`}
                    >
                        <div className="flex items-baseline justify-between mb-4 border-b border-border pb-3">
                            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
                                <span className="text-primary font-mono">{cat}</span>-game slip
                                <span className="ml-3 font-mono text-[10px] uppercase tracking-widest text-primary">
                                    VIP
                                </span>
                            </h2>
                            {!isLocked && (
                                <span className="font-mono text-xs uppercase tracking-widest text-zinc-500">
                                    {items.filter((i) => i.resultStatus === "won").length}/
                                    {items.length} won
                                </span>
                            )}
                        </div>
                        {isLocked ? (
                            <LockedTrack tier={cat} loggedIn={!!user} />
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {items.map((p, i) => (
                                    <ResultCard
                                        key={i}
                                        pick={p}
                                        date={yesterday.date}
                                        testIdPrefix={`result-${cat}-${i}`}
                                    />
                                ))}
                            </div>
                        )}
                    </section>
                );
            })}

            {/* 7-day trend */}
            {recent.length > 0 && (
                <section className="max-w-7xl mx-auto px-4 md:px-8 py-12">
                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-6 border-b border-border pb-3">
                        Last 7 days
                    </h2>
                    <div className="grid grid-cols-3 md:grid-cols-7 gap-2">
                        {recent.map((d) => {
                            const s = d.summary;
                            const settled = s.won + s.lost;
                            const rate = settled ? Math.round((s.won / settled) * 100) : 0;
                            return (
                                <div
                                    key={d.date}
                                    className="bg-card border border-border p-4 text-center"
                                    data-testid={`recent-${d.date}`}
                                >
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                                        {d.date.slice(5)}
                                    </div>
                                    <div className="font-mono text-2xl font-bold mt-1 text-primary">
                                        {settled ? `${rate}%` : "—"}
                                    </div>
                                    <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                                        {s.won}/{settled} · {s.total} picks
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}

            <section className="max-w-3xl mx-auto px-4 md:px-8 py-16 text-center">
                <h2 className="text-2xl md:text-4xl font-black uppercase tracking-tighter mb-4">
                    Want today's slip?
                </h2>
                <p className="text-zinc-400 mb-6">
                    Free 2-game pick every day. Upgrade to VIP for the 5 and 10-game
                    accumulators and the full slip history.
                </p>
                <Link to={user ? (isVip ? "/dashboard" : "/subscription") : "/register"}>
                    <Button
                        size="lg"
                        className="rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider px-8 h-12"
                        data-testid="results-cta-register"
                    >
                        {user ? (isVip ? "Go to dashboard →" : "Upgrade to VIP →") : "Get today's picks →"}
                    </Button>
                </Link>
            </section>
        </Layout>
    );
}

function LeagueChip({ children, active, onClick, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`px-3 py-1.5 text-xs font-mono uppercase tracking-widest border transition-snappy ${
                active
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-zinc-300 hover:border-primary hover:text-primary"
            }`}
        >
            {children}
        </button>
    );
}

function LockedTrack({ tier, loggedIn }) {
    return (
        <div
            className="bg-card border border-dashed border-primary/40 p-10 text-center relative overflow-hidden"
            data-testid={`locked-tier-${tier}`}
        >
            <LockSimple size={28} weight="duotone" className="text-primary mx-auto mb-3" />
            <h3 className="text-xl font-bold tracking-tight text-white mb-2">
                {tier}-game slip history — VIP only
            </h3>
            <p className="text-zinc-400 text-sm max-w-md mx-auto mb-6">
                Unlock the full track record (including the 5 and 10-game accumulators
                and every settled pick) with a KickLoom VIP subscription — ₦1,000/month.
            </p>
            <Link to={loggedIn ? "/subscription" : "/register"}>
                <Button
                    className="rounded-none bg-primary text-primary-foreground hover:bg-[#33FFB0] font-bold uppercase tracking-wider"
                    data-testid={`locked-tier-${tier}-cta`}
                >
                    <Crown size={14} weight="fill" className="mr-2" />
                    {loggedIn ? "Upgrade to VIP" : "Sign up & upgrade"}
                </Button>
            </Link>
        </div>
    );
}

function SummaryCell({ label, value, highlight, negative, mono }) {
    return (
        <div className={`bg-card border ${highlight ? "border-primary" : "border-border"} p-5`}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                {label}
            </div>
            <div
                className={`${mono ? "font-mono" : "font-mono"} text-2xl font-bold mt-1 ${
                    negative ? "text-red-400" : highlight ? "text-primary" : "text-white"
                }`}
            >
                {value}
            </div>
        </div>
    );
}

function ResultCard({ pick, date, testIdPrefix }) {
    const [whyOpen, setWhyOpen] = useState(false);
    const cfg = STATUS_CONFIG[pick.resultStatus] || STATUS_CONFIG.pending;
    const Icon = cfg.icon;
    const isWin = pick.resultStatus === "won";
    const reasoning = (pick.reasoning || "").trim();
    return (
        <article
            className="relative bg-card border border-border p-5 overflow-hidden"
            data-testid={`${testIdPrefix}-card`}
        >
            <div className={`absolute top-0 left-0 h-1 w-full ${cfg.bar}`} />
            <header className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                    <h3 className="text-base font-bold tracking-tight text-white truncate">
                        {pick.match}
                    </h3>
                    <p className="text-xs font-mono uppercase tracking-wider text-zinc-500 mt-1 truncate">
                        {pick.league}
                        {pick.leagueCountry ? ` · ${pick.leagueCountry}` : ""}
                    </p>
                </div>
                <div className={`flex items-center gap-1 ${cfg.color} flex-shrink-0`}>
                    <Icon size={16} weight="fill" />
                    <span className="font-mono text-xs font-bold uppercase tracking-widest">
                        {cfg.label}
                    </span>
                </div>
            </header>
            <div className="border-t border-border pt-3 space-y-2">
                <Row label="Pick" value={pick.prediction} accent />
                <Row label="Market" value={pick.market} />
                <Row label="Odds" value={Number(pick.odds || 0).toFixed(2)} mono />
                {pick.score && pick.score.home !== null ? (
                    <Row
                        label="Final"
                        value={`${pick.score.home} : ${pick.score.away}`}
                        mono
                        big
                    />
                ) : (
                    <Row label="Final" value="—" mono />
                )}
            </div>

            {reasoning && (
                <div className="border-t border-border mt-3 pt-3">
                    <button
                        type="button"
                        onClick={() => setWhyOpen((o) => !o)}
                        className="flex items-center justify-between w-full text-left"
                        aria-expanded={whyOpen}
                        data-testid={`${testIdPrefix}-why-toggle`}
                    >
                        <span className={`flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest ${cfg.color}`}>
                            <Lightbulb size={11} weight="fill" />
                            We called it because…
                        </span>
                        <CaretDown
                            size={12}
                            weight="bold"
                            className={`${cfg.color} transition-transform duration-200 ${whyOpen ? "rotate-180" : ""}`}
                        />
                    </button>
                    {whyOpen && (
                        <p
                            className="text-xs text-zinc-300 mt-2 leading-relaxed italic"
                            data-testid={`${testIdPrefix}-why-text`}
                        >
                            “{reasoning}”
                        </p>
                    )}
                </div>
            )}

            {isWin && (
                <div className="mt-4 pt-3 border-t border-border flex justify-end">
                    <ShareSlip pick={pick} date={date} testIdPrefix={testIdPrefix} />
                </div>
            )}
        </article>
    );
}

function Row({ label, value, accent, mono, big }) {
    return (
        <div className="flex items-baseline justify-between">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                {label}
            </span>
            <span
                className={`${mono ? "font-mono" : ""} ${big ? "text-base font-bold" : "text-sm"} ${
                    accent ? "text-primary font-semibold" : "text-white"
                } truncate ml-3`}
            >
                {value}
            </span>
        </div>
    );
}
